import { useEffect, useState } from "react";
import { X } from "lucide-react";

const COOKIE_NAME = "nx_cookie_consent";
const MAX_AGE = 60 * 60 * 24 * 365; // 1 year

const readConsent = () => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (!m) return null;
  try { return JSON.parse(decodeURIComponent(m[1])); } catch { return null; }
};

const writeConsent = (c) => {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(c))}; max-age=${MAX_AGE}; path=/; SameSite=Lax`;
};

const Toggle = ({ label, desc, checked, disabled, onChange, testid }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-white/10 last:border-0">
    <div>
      <p className="text-sm text-white font-medium">{label}{disabled && <span className="ml-2 text-[10px] tracking-[0.14em] uppercase text-nx-goldlight">Always on</span>}</p>
      <p className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`shrink-0 w-11 h-6 rounded-full relative transition-colors ${checked ? "bg-nx-goldlight" : "bg-white/15"} ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-nx-navy transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  </div>
);

export const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (readConsent()) return;
    const t = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(t);
  }, []);

  const save = (c) => {
    writeConsent({ essential: true, ...c, ts: new Date().toISOString() });
    setPrefsOpen(false);
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-0 inset-x-0 z-[80] bg-nx-navy border-t border-nx-goldlight/40 shadow-[0_-8px_40px_rgba(0,0,0,0.5)]" data-testid="cookie-banner">
        <div className="nx-wrap py-5 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <p className="text-sm text-nx-goldlight leading-relaxed flex-1" data-testid="cookie-banner-text">
            We use cookies to improve your experience on our website. By continuing to use this site, you agree to our use of cookies.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              type="button"
              onClick={() => save({ analytics: true, marketing: true })}
              className="bg-nx-goldlight hover:bg-nx-gold text-nx-navy font-semibold text-sm px-7 py-3 transition-colors"
              data-testid="cookie-accept-all"
            >
              Accept All
            </button>
            <button
              type="button"
              onClick={() => setPrefsOpen(true)}
              className="border border-nx-goldlight/70 text-nx-goldlight hover:bg-nx-goldlight/10 font-semibold text-sm px-7 py-3 transition-colors"
              data-testid="cookie-manage"
            >
              Manage Preferences
            </button>
          </div>
        </div>
      </div>

      {prefsOpen && (
        <div data-lenis-prevent className="fixed inset-0 z-[90] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="cookie-modal">
          <div className="w-full sm:max-w-md bg-nx-navy border border-nx-goldlight/40 p-6 sm:p-7">
            <div className="flex items-start justify-between mb-4">
              <h2 className="font-marcellus text-xl text-white">Cookie Preferences</h2>
              <button type="button" onClick={() => setPrefsOpen(false)} className="text-slate-400 hover:text-white p-1" aria-label="Close" data-testid="cookie-modal-close"><X size={19} /></button>
            </div>
            <Toggle label="Essential Cookies" desc="Required for the website to function — security, sessions and load balancing. These cannot be disabled." checked={true} disabled={true} onChange={() => {}} testid="cookie-toggle-essential" />
            <Toggle label="Analytics Cookies" desc="Help us understand how visitors use the site so we can improve it. Anonymous usage statistics only." checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testid="cookie-toggle-analytics" />
            <Toggle label="Marketing Cookies" desc="Used to measure campaigns and show relevant information. We do not run third-party ad trackers today." checked={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} testid="cookie-toggle-marketing" />
            <button
              type="button"
              onClick={() => save(prefs)}
              className="mt-6 w-full bg-nx-goldlight hover:bg-nx-gold text-nx-navy font-semibold text-sm px-7 py-3 transition-colors"
              data-testid="cookie-save-preferences"
            >
              Save Preferences
            </button>
          </div>
        </div>
      )}
    </>
  );
};
